import axios, { AxiosError } from "axios"
import { ServerResponse } from "http"
import jwt_decode from "jwt-decode"
import { NextApiResponse } from "next"
import { Account, Profile, User } from "next-auth"
import { SignOutParams, signOut } from "next-auth/react"

import axiosInstance from "../../../common/axiosInstance"
import {
  API_REFRESH_TOKEN_ENDPOINT,
  API_TOKEN_ENDPOINT,
  SB_LEGACY_LOGIN_URL,
  SB_LEGACY_SIGNOUT_URL,
  SUPPORT_INBOX_EMAIL_ADDRESS
} from "../../constants/configs"
import {
  DecodedTokenData,
  LoginResponse,
  SignInData,
  SignUpInput
} from "../models/auth"
import { HttpError, NetworkError } from "../utils/exceptions"
import {
  ACTIVATE_ACCOUNT_ENDPOINT,
  INITIATE_CHANGE_PASSWORD_ENDPOINT,
  INITIATE_PASSWORD_RESET_ENDPOINT,
  RESEND_ACTIVATION_LINK_ENDPOINT,
  RESET_PASSWORD_ENDPOINT,
  SIGNIN_ENDPOINT,
  SIGNUP_ENDPOINT,
  SOCIAL_AUTH_LOGIN_ENDPOINT,
  VERIFY_RESET_OTP_ENDPOINT
} from "./endpoints"
import { Credentials, JWTExtended, Provider } from "./types"

export class Auth {
  private static readonly TOKEN_REFRESH_BUFFER_MS = 15_000
  static DEFAULT_ERROR_MESSAGE = `Oops. Something went wrong. Please contact us at ${SUPPORT_INBOX_EMAIL_ADDRESS}.`

  public static async activateAccount(token: string, uid: string) {
    try {
      const response = await axiosInstance.post(ACTIVATE_ACCOUNT_ENDPOINT, {
        token,
        uid
      })
      return response.data
    } catch (error) {
      const axiosError = error as AxiosError
      const response = await axiosError.response
      if (!response?.status) throw new NetworkError()

      throw new HttpError(
        response.data as Record<string, string[]>,
        response.status
      )
    }
  }

  public static async validateResetPasswordOTP(email: string, otp: string) {
    try {
      const response = await axiosInstance.post(VERIFY_RESET_OTP_ENDPOINT, {
        email,
        otp
      })
      return response.data
    } catch (error) {
      const axiosError = error as AxiosError
      const response = await axiosError.response
      if (!response?.status) throw new NetworkError()

      throw new HttpError(
        response.data as Record<string, string[]>,
        response.status
      )
    }
  }
  public static async initiatePasswordReset(email: string) {
    try {
      const response = await axiosInstance.post(
        INITIATE_PASSWORD_RESET_ENDPOINT,
        {
          email
        }
      )
      return response.data
    } catch (error) {
      const axiosError = error as AxiosError
      const response = await axiosError.response
      if (!response?.status) throw new NetworkError()

      throw new HttpError(
        response.data as Record<string, string[]>,
        response.status
      )
    }
  }
  public static async initiateChangePassword(token: string) {
    try {
      const response = await axiosInstance.get(
        INITIATE_CHANGE_PASSWORD_ENDPOINT,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`
          }
        }
      )
      return response.data
    } catch (error) {
      const axiosError = error as AxiosError
      const response = await axiosError.response
      if (!response?.status) throw new NetworkError()

      throw new HttpError(
        response.data as Record<string, string[]>,
        response.status
      )
    }
  }
  public static async changePassword(
    email: string,
    password: string,
    otp: string,
    firstName?: string,
    lastName?: string,
    agreeToTerms?: boolean
  ) {
    try {
      const payload: any = {
        email,
        password,
        otp
      }
      if (firstName && lastName) {
        payload["first_name"] = firstName
        payload["last_name"] = lastName
      }
      if (typeof agreeToTerms !== "undefined") {
        payload["t_and_c"] = agreeToTerms
        payload["privacy_policy"] = agreeToTerms
      }

      const response = await axiosInstance.post(
        RESET_PASSWORD_ENDPOINT,
        payload
      )
      return response.data
    } catch (error) {
      const axiosError = error as AxiosError
      const response = await axiosError.response
      if (!response?.status) throw new NetworkError()

      throw new HttpError(
        response.data as Record<string, string[]>,
        response.status
      )
    }
  }

  public static async resendVerification(email: string) {
    try {
      const response = await axiosInstance.post(
        RESEND_ACTIVATION_LINK_ENDPOINT,
        {
          email
        }
      )
      return response.data
    } catch (error) {
      const axiosError = error as AxiosError
      const response = await axiosError.response
      if (!response?.status) throw new NetworkError()

      throw new HttpError(
        response.data as Record<string, string[]>,
        response.status
      )
    }
  }

  public static async signIn(loginInfo: SignInData) {
    const params = new URLSearchParams()
    Object.keys(loginInfo).forEach((key) => {
      params.append(key, loginInfo[key as keyof SignInData] as string)
    })

    await axiosInstance.post(SIGNIN_ENDPOINT, params, {
      headers: { "Content-type": "application/x-www-form-urlencoded" }
    })
  }

  public static async signup(registerInfo: SignUpInput) {
    try {
      const response = await axiosInstance.post(SIGNUP_ENDPOINT, {
        first_name: registerInfo.firstName,
        last_name: registerInfo.lastName,
        password: registerInfo.password,
        email: registerInfo.email,
        recaptcha: registerInfo.recaptcha,
        intent: registerInfo.intent?.toLowerCase()
      })

      return response.data
    } catch (error) {
      const axiosError = error as AxiosError

      const response = await axiosError.response
      if (!response?.status) throw new NetworkError()

      throw new HttpError(
        response.data as Record<string, string[]>,
        response.status
      )
    }
  }

  public static async exchangeCredentials(
    credentials: Credentials
  ): Promise<LoginResponse> {
    // tslint:disable-next-line:no-console
    console.log("Logging into the app...")
    const response = await fetch(API_TOKEN_ENDPOINT, {
      method: "POST",
      body: JSON.stringify(credentials),
      headers: { "Content-Type": "application/json" }
    })
    const responseJSON = await response.json()

    if (!response.ok) {
      if (response.status === 401) {
        const message = responseJSON.detail
        const code = responseJSON.code
        throw new HttpError({
          message,
          code,
          email: encodeURIComponent(credentials.username)
        })
      } else if (response.status === 400) {
        throw new HttpError({
          message: "Failed to authenticate",
          errors: responseJSON
        })
      }
      throw new Error("Failed to authenticate ")
    }
    // tslint:disable-next-line:no-console
    console.log("Login successful...")
    return responseJSON
  }

  public static async exchangeSocialIdToken(
    token: JWTExtended,
    user: User,
    account: Account,
    profile?: Profile
  ): Promise<JWTExtended> {
    // see docs: https://next-auth.js.org/tutorials/refresh-token-rotation
    // initial sign in
    // tslint:disable-next-line:no-console
    console.warn("Exchanging social ID token for access token...")
    let exchanged
    try {
      const response = await axiosInstance.post(SOCIAL_AUTH_LOGIN_ENDPOINT, {
        provider: account.provider as Provider,
        first_name: profile?.given_name as string,
        last_name: profile?.family_name as string,
        id_token: user.id_token as string,
        access_token: user.access_token as string,
        refresh_token: user.refresh_token as string,
        os: "web"
      })
      exchanged = response.data as LoginResponse
    } catch (error: any) {
      throw new Error(error.message || Auth.DEFAULT_ERROR_MESSAGE)
    }

    const exchangedToken = jwt_decode<DecodedTokenData>(exchanged.token)
    exchangedToken.isNewUser = exchanged.is_new_user
    return {
      ...token,
      data: exchangedToken,
      accessToken: exchanged.token,
      accessTokenExpires: exchangedToken.exp * 1000,
      refreshToken: exchanged.refresh
    }
  }

  public static async signout(opts: SignOutParams<any>) {
    try {
      await axios.post(SB_LEGACY_SIGNOUT_URL)
    } catch (error) {
      // tslint:disable-next-line:no-console
      console.error(error)
    }

    await signOut(opts)
  }

  public static async legacyLogin(
    res: NextApiResponse | ServerResponse,
    token: string
  ) {
    const response = await fetch(SB_LEGACY_LOGIN_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      }
    })

    /**
     * usage of getSetCookie() method worked after upgrading to nextjs v13/Node v16.
     * FYI: this method is not officially documented as a valid api on the node-fetch polyfill for node.js
     *
     * @see https://developer.mozilla.org/en-US/docs/Web/API/Headers/getSetCookie
     */
    const cookie = (response.headers as any).getSetCookie()
    if (!cookie.length) {
      console.warn("Couldn't login. Cookie was not set")
      console.warn(response.status, response.statusText)
      throw new Error("Failed to login")
    }
    res.setHeader("Set-Cookie", cookie)
    console.log("Logged into legacy app.")
  }

  public static async refreshAccessToken(refresh: string) {
    console.warn("Refreshing access token...")
    try {
      const response = await axiosInstance.post(API_REFRESH_TOKEN_ENDPOINT, {
        refresh
      })
      return response.data
    } catch (error) {
      return { error }
    }
  }

  public static async refreshTokenIfExpired(token: JWTExtended) {
    // Return previous token if the access token has not expired yet.
    // Buffer of 15 s ensures we refresh before Django's JWT leeway window kicks in.
    if (
      token.accessTokenExpires &&
      Date.now() < token.accessTokenExpires - Auth.TOKEN_REFRESH_BUFFER_MS
    ) {
      console.warn("Access token has not expired. Will reuse.")
      return token
    }

    // Access token has expired, try to update it
    const refreshed = await Auth.refreshAccessToken(token.refreshToken)
    if (refreshed.error || !refreshed.token) {
      return { ...token, error: "RefreshAccessTokenError" as const }
    }
    const refreshedToken = jwt_decode<DecodedTokenData>(refreshed.token)
    return {
      ...token,
      data: refreshedToken,
      accessToken: refreshed.token,
      accessTokenExpires: refreshedToken.exp * 1000,
      refreshToken: refreshed.refresh
    }
  }

  public static async enrichCredentialsToken(token: JWTExtended, user: User) {
    // credentials provider
    return {
      ...token,
      data: user.data as DecodedTokenData,
      accessToken: user.accessToken as string,
      accessTokenExpires: (user.data as DecodedTokenData).exp * 1000,
      refreshToken: user.refreshToken as string
    }
  }
}
